import { Button, Form, FormGroup, FormParagraph } from '@ksyos/design-system';
import { useState } from 'react';
import type { IPractitioner } from '@frontend/components/interfaces/adminProcessesList';
import type { IEmployment } from '../../interfaces/employment';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { getSelectedLanguage } from '../../../translate';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import { DropdownSearch, Paragraph } from '../../elements/common';
import { Modal } from '../../elements/modals/Modal';

interface ICheckGeneralPractitionerModalProps {
    onClose: () => Promise<void>;
    requesterOrganization: string;
    requesterOrganizationId: string | null;
    requesterPractitioner: IPractitioner;
    requestId: string;
}

export function CheckGeneralPractitionerModal(props: ICheckGeneralPractitionerModalProps) {
    const language = getSelectedLanguage();
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(props.requesterOrganizationId);
    const [isSaving, setIsSaving] = useState(false);
    const abortableFetch = useAbortableFetch();

    return (
        <Modal
            actionButtons={[
                <Button
                    key="cancel"
                    onClick={props.onClose}
                    variant="outline"
                >
                    {language.common.cancel}
                </Button>,
                <Button
                    isDisabled={isSaving || selectedOrganizationId === null}
                    key="carry-on-process"
                    onClick={async () => await updateCareProvider()}
                >
                    {language.request.carryOnProcess}
                </Button>,
            ]}
            isOpen
            onClose={async () => await props.onClose()}
            size="medium"
            title={language.request.checkGeneralPractitioner}
        >
            <Paragraph>{language.checkGeneralPractitionerModal.text}</Paragraph>

            <Form submitOnEnter={false}>
                <FormGroup label="Huisarts">
                    <FormParagraph>{getDisplayName(props.requesterPractitioner)}</FormParagraph>
                </FormGroup>

                <FormGroup label="Huisartspraktijk">
                    <DropdownSearch
                        clearable={false}
                        loadOptions={loadCareProviderOrganizations}
                        newstyles
                        onChange={async (value) => setSelectedOrganizationId(value)}
                        value={selectedOrganizationId}
                    />
                </FormGroup>
            </Form>
        </Modal>
    );

    async function loadCareProviderOrganizations() {
        if (props.requesterPractitioner.id === undefined) {
            return [];
        }

        const url = `/api/employments/${props.requesterPractitioner.id}/organizations?isActive=true&isGeneralPractitionerOrHidha=true`;
        const results = await abortableFetch.fetchJson(url, 'GET');
        const options = results.json.items.map((employment: IEmployment) => ({
            label: employment.organization?.name,
            value: employment.organization?.id,
        }));

        return options;
    }

    async function updateCareProvider() {
        setIsSaving(true);
        await abortableFetch.postJson(`api/requests/${props.requestId}/updateTelediaRequest`, {
            requesterOrganizationId: selectedOrganizationId,
            requesterPractitionerId: props.requesterPractitioner.id,
        });
        setIsSaving(false);
        props.onClose();
    }
}
